import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { backend } from '@/api/backend';
import { ApiError, newIdempotencyKey } from '@/api/http';
import { Button, Panel, PanelHeader } from '@/components/ui';
import {
  normalisePrefix,
  overlaps,
  registerBody,
  scopeLabel,
  whyNotRegistrable,
} from '@/pages/registration';

/**
 * Register a scope the account listing has not observed, and choose the folder.
 *
 * Two things made this necessary at once.
 *
 * The listing is not the only way in. Registration takes a name, an account, a
 * region and a prefix, and needs none of them to have been observed first -
 * but every path in the UI offered registration only for a row the listing had
 * already returned. On an estate whose observed catalogue is empty, and that is
 * the state a fresh deployment starts in, there was no way to register
 * anything at all. The one screen that exists to point the product at a bucket
 * could not point it at one.
 *
 * And a scope has always been `(account, bucket, prefix)` rather than a bucket.
 * `buckets.scope_prefix` is part of the unique key, discovery lists inside it
 * and the walk carries it; the picker simply never asked. So a bucket and one folder inside it are two separate scopes, each with its own
 * discovery, walk, package and handover, and neither can see the other. That is
 * the difference between a report about a bucket and a report about a thing.
 */
export function RegisterScope({
  accountId,
  region,
  existing,
}: {
  /** Read from the listing or from a registration; null when genuinely unknown. */
  accountId: string | null;
  /** This account's usual region, offered as the default. */
  region: string | null;
  /** Registered scopes, for the overlap warning. */
  existing: readonly { name: string; prefix: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [account, setAccount] = useState(accountId ?? '');
  const [inRegion, setInRegion] = useState(region ?? '');
  const [prefix, setPrefix] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const scope = { name: name.trim(), accountId: account.trim() || null, region: inRegion.trim() || null, prefix };
  const refusal = name.trim() === '' ? null : whyNotRegistrable(scope);
  const clashes = name.trim() === '' ? [] : overlaps({ name: scope.name, prefix }, existing);

  const submit = async () => {
    const why = whyNotRegistrable(scope);
    if (why) {
      setNote(why);
      return;
    }
    setBusy(true);
    setNote(null);
    try {
      const r = await backend.registerBucket(registerBody(scope), newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: ['live-buckets'] });
      void qc.invalidateQueries({ queryKey: ['account-buckets'] });
      navigate(`/buckets/${r.bucket.id}`);
    } catch (e) {
      setNote(e instanceof ApiError ? `${scope.name}: ${e.message}` : `${scope.name}: the API did not respond.`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Panel>
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
          <span className="min-w-0 flex-1 text-small text-ink-2">
            Working on a bucket the listing has not seen, or on one folder inside a bucket?
          </span>
          <Button variant="ghost" onClick={() => setOpen(true)}>
            Register a scope by name
          </Button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelHeader
        title="Register a scope"
        sub="a scope is an account, a bucket and a folder; leave the folder empty for the whole bucket"
      />
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <Field label="Bucket name" hint="exactly as it is in S3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="the name as it is in S3"
            className={INPUT}
            autoComplete="off"
          />
        </Field>
        <Field label="AWS account" hint="the account the bucket lives in">
          <input
            value={account}
            onChange={(e) => setAccount(e.target.value)}
            placeholder="twelve digits"
            className={INPUT}
            inputMode="numeric"
          />
        </Field>
        <Field label="Region" hint="a bucket in another region reads nothing">
          <input value={inRegion} onChange={(e) => setInRegion(e.target.value)} placeholder="the bucket's region" className={INPUT} />
        </Field>
        <Field label="Folder" hint="empty for the whole bucket">
          <input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="a folder path, ending in /"
            className={INPUT}
          />
        </Field>
      </div>

      {/* What will actually be registered, spelled out, because the trailing
          slash is added silently and an operator should see the scope they are
          about to create rather than the text they typed. */}
      {name.trim() !== '' && (
        <p className="px-4 pb-2 text-caption text-ink-3">
          Registers <span className="mono text-ink-2">{scopeLabel(scope.name, normalisePrefix(prefix))}</span>
          {normalisePrefix(prefix) === '' ? ' — the whole bucket.' : ' — that folder only, as its own scope.'}
        </p>
      )}

      {clashes.length > 0 && (
        <p role="status" className="mx-4 mb-2 rounded border border-warn/40 bg-warn-soft px-3 py-2 text-small text-ink-2">
          This overlaps {clashes.map((c) => scopeLabel(c.name, c.prefix)).join(', ')}, which is already registered.
          Objects in both belong to both scopes and would be handed over twice. That is allowed and it is your call;
          it is said here so it is not a surprise later.
        </p>
      )}

      {(refusal || note) && (
        <p role="alert" className="px-4 pb-2 text-small text-warn">
          {note ?? refusal}
        </p>
      )}

      <div className="flex items-center gap-3 border-t border-line px-4 py-2.5">
        <Button onClick={() => void submit()} loading={busy} disabled={busy || name.trim() === '' || Boolean(refusal)}>
          {busy ? 'Registering…' : 'Register this scope'}
        </Button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-small text-ink-3 transition-colors duration-fast hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </Panel>
  );
}

const INPUT =
  'mono w-full rounded border border-line bg-panel-2 px-2.5 py-1.5 text-small text-ink outline-none placeholder:text-ink-4 focus:border-acc-2';

function Field({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-caption text-ink-2">
        {label} <span className="text-ink-4">· {hint}</span>
      </span>
      {children}
    </label>
  );
}
